/**
 * Hook pour le prévisionnel de trésorerie
 * Utilise React Query pour le cache
 */

import { useQuery } from '@tanstack/react-query';
import { forecastApi, cashApi, simulationApi } from '@/services/api';
import type { SimulationInput } from '@/types';
import { config } from '@/config';
import { useMutationWithToast } from './useMutationWithToast';

// ========================================
// Query Keys
// ========================================

export const forecastKeys = {
    all: ['forecast'] as const,
    data: (days: number) => [...forecastKeys.all, days] as const,
};

export const cashKeys = {
    all: ['cash'] as const,
    balance: () => [...cashKeys.all, 'balance'] as const,
};

export const simulationKeys = {
    all: ['simulation'] as const,
};

// ========================================
// Hooks
// ========================================

/**
 * Données prévisionnelles sur N jours
 */
export function useForecast(days: number = config.forecastDays) {
    return useQuery({
        queryKey: forecastKeys.data(days),
        queryFn: () => forecastApi.get(days),
        staleTime: 60_000, // 1 minute
        refetchOnWindowFocus: true,
    });
}

/**
 * Solde actuel
 * Note: GET /cash/balance n'existe pas - utiliser forecast.currentBalance
 */
// export function useCashBalance() {
//     return useQuery({
//         queryKey: cashKeys.balance(),
//         queryFn: () => cashApi.getBalance(),
//         staleTime: 60_000,
//     });
// }

/**
 * Mettre à jour le solde actuel
 */
export function useUpdateCashBalance() {
    return useMutationWithToast({
        mutationFn: ({ balance, date }: { balance: number; date: string }) =>
            cashApi.setBalance(balance, date),
        operationName: 'Mise à jour solde',
        successMessage: 'Solde mis à jour avec succès',
        invalidateKeys: [cashKeys.balance(), forecastKeys.all],
    });
}

/**
 * Simuler un chantier
 */
export function useSimulation() {
    return useMutationWithToast({
        mutationFn: (input: SimulationInput) => simulationApi.simulate(input),
        operationName: 'Simulation',
        // Pas de successMessage car la simulation retourne des données à afficher
    });
}
